import { ASC, centerCalculator, GestCard, globalCenter, ICity, ICityStats, ICoordinates, ISharedConfig, ITypeCount, slottedColor } from '@shared/library';
import { languages } from '@shared/library/dist/static/language';
import axios from 'axios';
import React from 'react';
import config from '../../../../../../config';
import importer from '../../../../../../helpers/importer';
import './Home.scss';
//#region COMPONENTS
import { Divider } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { InfoWindow } from '@react-google-maps/api';
import GestGaugeChart from '@shared/library/dist/components/gest/GestCharts/gauge/GestGaugeChart';
import GestContextMenu from '@shared/library/dist/components/gest/GestContextMenu/GestContextMenu';
import GestSelect from '@shared/library/dist/components/gest/GestInputs/select/GestSelect';
import GestMap from '@shared/library/dist/components/gest/GestMap/GestMap';
import GestRowStats from '@shared/library/dist/components/gest/GestRowStat/GestRowStats';
import Title from '@shared/library/dist/components/gest/Title/Title';
import { ValueOnMax } from '../../../../../gest';
//#endregion
//#region MODELS
import { GridColDef } from '@mui/x-data-grid';
import { IGestSelectOption } from '@shared/library/dist/components/gest/GestInputs/select/GestSelectProps';
import { IPolygon } from '@shared/library/dist/components/gest/GestMap/GestMapProps';
import { MenuArea_model } from '../../../../../../models/ui_data/MenuArea_model';
import { LangKey } from '../../../../../../models/ui_data/Lang_model';
//#endregion
//ICONS
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { setAndStoreCities, setAndStoreSelectedCity } from '@webgest/library';
import { ReactComponent as ConsumptionMonitorIcon } from "../../../../../../assets/images/icons/consumption_monitor_Blue.svg";
import { ReactComponent as WFMInterventionManagmentIcon } from "../../../../../../assets/images/icons/intervention_management_Blue.svg";
import { ReactComponent as NetworkAnalizerIcon } from "../../../../../../assets/images/icons/network_analysis_option_Blue.svg";
import { ReactComponent as WFMPlanningIcon } from "../../../../../../assets/images/icons/planning_Blue.svg";

const Home = (props: ISharedConfig) => {
    //#region STATIC DATA
    const lang = languages(props.settings?.lang);
    document.title = config.app.name;
    let debounceTimer: any | null = null;

    const allCities = { value: -1, label: lang.all_towns };
    const menu: MenuArea_model[] = JSON.parse(localStorage.getItem('menu') ?? '[]');
    const periods: IGestSelectOption<undefined>[] = [
        {
            value: 0,
            label: lang.realtime
        },
        {
            value: 1,
            label: lang.yesterday
        },
        {
            value: 7,
            label: lang.last_7_days
        },
        {
            value: 30,
            label: lang.last_30_days
        }
    ];
    const defaultStats: ICityStats = {
        metersCount: 0,
        activeUsers: 0,
        smartMeters: 0,
        nonSmart: 0,
        readingCount: 0,
        KPI: 0,
        alarmCount: 0,
        alarms: [],
        communications: [],
        brands: [],
        types: [],
    };
    //#endregion

    //#region STATES
    const [selectedStatPeriod, setSelectedStatPeriod] = React.useState(periods[2]);
    const [center, setCenter] = React.useState<ICoordinates>();

    //CITIES
    const [mapLabel, setMapLabel] = React.useState<{ id: number; label?: string; position: ICoordinates; }>();
    const [mapStats, setMapStats] = React.useState(defaultStats);
    const [citiesWithStats, setCitiesWithStats] = React.useState<ICity[]>([]);

    //CONTEXT MENU
    const [rightClickedCityPosition, setRightClickedCityPosition] = React.useState<{ x: number; y: number; }>();
    const [rightClickedCity, setRightClickedCity] = React.useState<ICity>();

    //FLAGS
    const [statsLoading, setStatsLoadings] = React.useState(false);
    const [showMapStatsDetails, setShowMapStatsDetails] = React.useState(false);
    //#endregion

    //#region METHODS

    //#region API CALLS
    const getCitiesStatsAPI = (conf?: ISharedConfig, period?: IGestSelectOption<undefined>) => {
        conf ??= props;
        const city = conf.cache!.cities.selected!;

        if (period) {
            setSelectedStatPeriod(period);
        }

        setStatsLoadings(true);
        return axios.get<ICity[]>(`${config.api.internal}/Home/Stats/Last${(period ?? selectedStatPeriod).value}Days`, { withCredentials: true, cancelToken: conf.session?.cancelApiConnection?.token })
            .then(res => {
                const stats = res.data;
                const cities = conf.cache?.cities.options.map<IGestSelectOption<ICity>>(c => {
                    const s = stats.find(fc => fc.id === c.value);
                    const metersCount = (s?.smartMeters ?? 0) + (s?.nonSmart ?? 0);
                    const data = { ...c.data!, ...s, metersCount };

                    return { ...c, data, labelAdditional: metersCount };
                }) ?? [];
                calcStats(cities, city);
                setCitiesWithStats(cities.map(c => c.data!));

                if (!conf.cache?.cities.options.some(o => o.labelAdditional)) {
                    setAndStoreCities(conf, cities);
                }
            })
            .catch(err => console.error(err))
            .finally(() => setStatsLoadings(false));
    };
    //#endregion

    const handleMouseMove = (c: ICity, coords: ICoordinates) => { //  debounce dell'evento
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }

        if (!debounceTimer && !mapLabel) {
            debounceTimer = setTimeout(() => {
                const label = {
                    id: c.id as number,
                    label: c.name,
                    position: coords
                };
                setMapLabel(label);
                clearTimeout(debounceTimer);
                debounceTimer = null;
            }, 375); // 375 millisecondi di ritardo
        }
    };

    const handleMouseOut = () => {
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }
        setMapLabel(undefined);
    };

    const calcStats = (cities?: IGestSelectOption<ICity>[], selectedCity?: IGestSelectOption<ICity>) => {
        selectedCity ??= props.cache?.cities.selected;
        cities ??= props.cache?.cities.options;

        if (cities?.length && selectedCity?.value) {
            let mapStatsTMP = structuredClone(defaultStats);

            if (selectedCity.value !== -1) {
                //clicked city's stats
                const city = cities.find(c => c.value === selectedCity.value)?.data;

                if (city) {
                    mapStatsTMP = {
                        ...city,
                        alarms: city.alarms
                            ?.map(a => ({ ...a, perc: parseFloat(((a.count / city.metersCount!) * 100).toFixed(2)) }))
                            .sort(ASC('type')) ?? [],
                        communications: city.communications
                            ?.map(c => ({ ...c, perc: parseFloat(((c.count / city.metersCount!) * 100).toFixed(2)) }))
                            .sort(ASC('type')) ?? [],
                        brands: city.brands
                            ?.map(b => ({ ...b, perc: parseFloat(((b.count / city.metersCount!) * 100).toFixed(2)) }))
                            .sort(ASC('type')) ?? [],
                        types: city.types
                            ?.map(t => ({ ...t, perc: parseFloat(((t.count / city.metersCount!) * 100).toFixed(2)) }))
                            .sort(ASC('type')) ?? [],
                    };

                    if (city.geoArea) {
                        setCenter(centerCalculator(city.geoArea));
                    }
                }
            }
            else {
                //all cities stats
                cities.reduce((acc, c) => {
                    if (c.data) {
                        acc.metersCount! += c.data.metersCount!;
                        acc.activeUsers! += c.data.activeUsers ?? 0;
                        acc.smartMeters! += c.data.smartMeters ?? 0;
                        acc.nonSmart! += c.data.nonSmart ?? 0;
                        acc.readingCount! += c.data.readingCount ?? 0;
                        acc.KPI! += c.data.KPI ?? 0;
                        acc.alarmCount! += c.data.alarmCount ?? 0;

                        c.data.alarms?.forEach(a => {
                            const index = acc.alarms!.findIndex(v => v.type === a.type);

                            if (index >= 0) {
                                acc.alarms![index].count += a.count;
                            } else {
                                acc.alarms!.push({ ...a });
                            }
                        });
                        c.data.communications?.forEach(cc => {
                            const index = acc.communications!.findIndex(v => v.type === cc.type);

                            if (index >= 0) {
                                acc.communications![index].count += cc.count;
                            } else {
                                acc.communications!.push({ ...cc });
                            }
                        });
                        c.data.brands?.forEach(b => {
                            const index = acc.brands!.findIndex(v => v.type === b.type);

                            if (index >= 0) {
                                acc.brands![index].count += b.count;
                            } else {
                                acc.brands!.push({ ...b });
                            }
                        });
                        c.data.types?.forEach(t => {
                            const index = acc.types!.findIndex(v => v.type === t.type);

                            if (index >= 0) {
                                acc.types![index].count += t.count;
                            } else {
                                acc.types!.push({ ...t });
                            }
                        });
                    }

                    return acc;
                }, mapStatsTMP);

                mapStatsTMP.KPI = parseFloat(((mapStatsTMP.readingCount! / (mapStatsTMP.activeUsers ?? 1)) * 100).toFixed(2));
                mapStatsTMP.alarms = mapStatsTMP.alarms!
                    .map(a => ({ ...a, perc: parseFloat(((a.count / mapStatsTMP.metersCount!) * 100).toFixed(2)) }))
                    .sort(ASC('type'));

                mapStatsTMP.communications = mapStatsTMP.communications!
                    .map(c => ({ ...c, perc: parseFloat(((c.count / mapStatsTMP.metersCount!) * 100).toFixed(2)) }))
                    .sort(ASC('type'));

                mapStatsTMP.brands = mapStatsTMP.brands!
                    .map(b => ({ ...b, perc: parseFloat(((b.count / mapStatsTMP.metersCount!) * 100).toFixed(2)) }))
                    .sort(ASC('type'));

                mapStatsTMP.types = mapStatsTMP.types!
                    .map(t => ({ ...t, perc: parseFloat(((t.count / mapStatsTMP.metersCount!) * 100).toFixed(2)) }))
                    .sort(ASC('type'));
            }

            setMapStats(mapStatsTMP);
        }
    };

    const findPerm = (areaID: number, sectionID: number) => Boolean(menu.find(m => m.id === areaID)?.subs.find(o => !o.custom && o.id === sectionID && o.enabled));

    const updateSelectedMenuTool = (localLabel: string, localValue: number | IGestSelectOption<ICity>, path: string) => {
        if (localValue) {
            localStorage.setItem(localLabel, (localValue as IGestSelectOption<ICity>).value ? JSON.stringify(localValue as IGestSelectOption<ICity>) : (localValue as number).toString());
            window.open(`/${path}`, "_blank");
        }
    };

    const rowStatsColumns = (headerName: string): GridColDef<ITypeCount>[] => [
        { headerName: lang[headerName as LangKey], field: "type", renderCell: params => lang.alarmsDictionary[params.row.type as keyof typeof lang.alarmsDictionary] || params.row.type || "N/D", flex: 1, },
        { headerName: "", type: "number", field: "count", width: 65, cellClassName: "bold azure" },
        { headerName: "", type: "number", field: "perc", width: 65, cellClassName: "bold green", renderCell: params => `${params.row.perc?.toLocaleString()}%` },
    ];
    //#endregion

    //STATES CHANGE
    //init
    React.useEffect(() => {
        if (props.cache?.cities?.options && props.cache.cities.options.length !== citiesWithStats.length) {
            const cities = props.cache!.cities.options.map(o => o.data!);
            setCenter(globalCenter(cities));
            setCitiesWithStats(cities);

            const config = setAndStoreSelectedCity(props, allCities);
            getCitiesStatsAPI(config);
        }
    }, [props.cache?.cities?.options.length]);

    return <div id="home" className='flex fitContent' onContextMenu={e => e.preventDefault()}>
        <GestCard id="map" class='fitContent'>
            <GestMap
                drawedMap
                zoom={!props.cache?.cities?.selected?.value || props.cache?.cities?.selected?.value === -1 ? 10 : 12}
                center={center || { lat: 45.306622, lng: 10.416617 }}
                buttons={[{ tooltip: "Statistiche", icon: <TravelExploreIcon />, onclick: () => setShowMapStatsDetails(!showMapStatsDetails) }]}
                polygons={{
                    areas: citiesWithStats
                        .map<IPolygon>(c => ({
                            points: c.geoArea!,
                            options: {
                                fillColor: c.KPI !== undefined && c.KPI >= 0 ? `rgb(${slottedColor(c.KPI).join(',')})` : undefined,
                                ...(c.id === props.cache?.cities.selected?.value
                                    ? { strokeColor: '#063044', zIndex: 2, strokeWeight: 2 }
                                    : undefined
                                )
                            },
                            onClick: e => {
                                const cityOptions = citiesWithStats.map<IGestSelectOption<ICity>>(cc => ({ value: cc.id, label: cc.name, data: cc }));
                                const selected = props.cache?.cities.selected?.value === c.id
                                    ? { value: -1, label: lang.all_towns }
                                    : cityOptions.find(o => o.value === c.id)!;
                                e?.latLng && setCenter({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                                setAndStoreSelectedCity(props, selected);
                                calcStats(cityOptions, selected);
                            },
                            onRightClick: e => {
                                e.domEvent.preventDefault();
                                e.domEvent.stopPropagation();

                                const { clientX, clientY } = e.domEvent as MouseEvent;
                                setRightClickedCityPosition({ x: clientX, y: clientY });
                                setRightClickedCity(c);
                            },
                            onMouseOver: e => {
                                const lat = e.latLng?.lat();
                                const lng = e.latLng?.lng();

                                if (lat && lng) {
                                    handleMouseMove(c, { lat, lng });
                                }
                            },
                            onMouseOut: handleMouseOut
                        }))
                }}
            >
                {showMapStatsDetails && props.cache?.cities.options
                    ?.filter(c => c.data?.geoArea?.length && c.data?.areaLabelCoords && c.data.KPI !== undefined)
                    .map((c, i) =>
                        <InfoWindow
                            key={i}
                            position={c.data!.areaLabelCoords}
                            zIndex={1}
                        >
                            <span className='mapLabel stat'>
                                <div className='value'>
                                    {`${Math.ceil(c.data!.KPI!)}%`}
                                </div>
                            </span>
                        </InfoWindow>
                    )}

                {mapLabel &&
                    <InfoWindow
                        position={mapLabel.position}
                        zIndex={2}
                    >
                        <span className='mapLabel'>{mapLabel.label}</span>
                    </InfoWindow>
                }

                <GestContextMenu
                    position={rightClickedCityPosition}
                    label={rightClickedCity?.name}
                    items={[
                        /*  NETWORK */
                        {
                            label: lang.network_analysis,
                            icon: <NetworkAnalizerIcon />,
                            onclick: () => updateSelectedMenuTool("networkAnalisysCity", rightClickedCity!.id as number, "networkAnalysis"),
                            disabled: !findPerm(1, 2),
                        },
                        /*  MONITORS */
                        {
                            label: lang.consumption_monitor,
                            icon: <ConsumptionMonitorIcon />,
                            onclick: () => updateSelectedMenuTool("Gest_Selected_Cities", { value: rightClickedCity!.id, label: rightClickedCity!.name }, "consumptionMonitor"),
                            disabled: !findPerm(2, 6)
                        },
                        /*  WFM */
                        {
                            label: lang.intervention_management,
                            icon: <WFMInterventionManagmentIcon />,
                            onclick: () => updateSelectedMenuTool("WFMinterventionManagementArea", rightClickedCity!.id as number, "interventionManagement"),
                            disabled: !findPerm(6, 15),
                        },
                        {
                            label: lang.planning,
                            icon: <WFMPlanningIcon />,
                            onclick: () => updateSelectedMenuTool("WFMplanningArea", rightClickedCity!.id as number, "planning"),
                            disabled: !findPerm(6, 16),
                        },
                    ]}
                />

                <GestCard class='legend' title={'kpi'}>
                    <span>{'> 85%'}</span>
                    <span>{'> 70% & <= 85%'}</span>
                    <span>{'> 50% & <= 70%'}</span>
                    <span>{'> 0% & <= 50%'}</span>
                    <span>{'0%'}</span>
                </GestCard>

                <GestSelect
                    className='periodSelector'
                    loading={props.cache?.cities.loading || statsLoading}
                    value={selectedStatPeriod.value}
                    options={periods}
                    onchange={selected => getCitiesStatsAPI(undefined, selected[0])}
                    required
                />
            </GestMap>

            <div className='mapStats flex'>
                <GestCard>
                    <div className='stats'>
                        <div className='values'>
                            <Title>{lang.end_users}</Title>
                            <h3>
                                {statsLoading ? <Skeleton /> : mapStats.metersCount?.toLocaleString()}
                            </h3>
                        </div>
                        <div className="icon">
                            <img src={importer.ic.require('utentiAttivi.svg')} alt="" loading='lazy' />
                        </div>
                    </div>
                </GestCard>

                <GestCard>
                    <div className='stats'>
                        <div className='values'>
                            <Title>{lang.smart_meter}</Title>
                            <h3>
                                {statsLoading ? <Skeleton /> : mapStats.smartMeters?.toLocaleString()}
                            </h3>
                        </div>
                        <div className="icon">
                            <img src={importer.ic.require('smartMeter.svg')} alt="" loading='lazy' />
                        </div>
                    </div>
                </GestCard>

                <GestCard>
                    <div className='stats'>
                        <div className='values'>
                            <Title>{lang.non_smart}</Title>
                            <h3>
                                {statsLoading ? <Skeleton /> : mapStats.nonSmart?.toLocaleString()}
                            </h3>
                        </div>
                        <div className="icon">
                            <img src={importer.ic.require('meternonSmart.svg')} alt="" loading='lazy' />
                        </div>
                    </div>
                </GestCard>

                <GestCard>
                    <div className='stats'>
                        <div className='values'>
                            <Title>{lang.red_meters}</Title>
                            <h3>
                                {statsLoading ? <Skeleton /> : mapStats.readingCount?.toLocaleString()}
                            </h3>
                        </div>
                        <div className="icon">
                            <img src={importer.ic.require('lettureValide.svg')} alt="" loading='lazy' />
                        </div>
                    </div>
                </GestCard>

                <GestCard>
                    <div className='stats'>
                        <div className='values'>
                            <Title>{lang.kpi}</Title>
                            <h3 style={{ color: mapStats.KPI !== undefined ? `rgb(${slottedColor(mapStats.KPI).join(',')})` : 'rgb(127, 127, 127)' }} >
                                {statsLoading ? <Skeleton /> : (mapStats.KPI !== undefined && !isNaN(mapStats.KPI) ? mapStats.KPI : 0).toLocaleString() + "%"}
                            </h3>
                        </div>
                        <div className="icon">
                            <img src={importer.ic.require('kpiPercent.svg')} alt="" loading='lazy' />
                        </div>
                    </div>
                </GestCard>

                <GestCard>
                    <div className='stats'>
                        <div className='values'>
                            <Title>{lang.active_alarms}</Title>
                            <h3>
                                {statsLoading ? <Skeleton /> : mapStats.alarmCount?.toLocaleString()}
                            </h3>
                        </div>
                        <div className="icon">
                            <img src={importer.ic.require('alarms.svg')} alt="" loading='lazy' />
                        </div>
                    </div>
                </GestCard>
            </div>
        </GestCard>

        <GestCard id='sideStats' class='fullh'>
            <GestSelect
                fullw
                required
                placeholder={lang.towns}
                loading={props.cache?.cities.loading}
                value={props.cache?.cities.selected?.value ?? -1}
                options={[
                    allCities,
                    ...(citiesWithStats.length
                        ? citiesWithStats.map<IGestSelectOption<ICity>>(c => ({ value: c.id, label: c.name, data: c, labelAdditional: c.metersCount }))
                        : props.cache?.cities.options ?? []
                    )
                ]}
                onchange={selected => {
                    calcStats(citiesWithStats.map<IGestSelectOption<ICity>>(c => ({ value: c.id, label: c.name, data: c, labelAdditional: c.metersCount })), selected[0]);
                    setAndStoreSelectedCity(props, selected[0]);
                }}
            />

            <Divider style={{ margin: 0 }} />
            <div className='justify-content-between'>
                <div className='fullw'>
                    <Title>{lang.smart_meter}</Title>
                    {statsLoading ? <Skeleton /> : <ValueOnMax max={mapStats.metersCount ?? 0} value={mapStats.smartMeters ?? 0} />}
                </div>

                <GestGaugeChart
                    loading={statsLoading}
                    perc
                    h={80}
                    max={mapStats.metersCount}
                    value={statsLoading ? 0 : mapStats.smartMeters}
                />
            </div>

            <Divider style={{ margin: 0 }} />
            <GestRowStats style={{ width: "calc(20vw - 20px)" }} loading={statsLoading} rows={mapStats.brands} columns={rowStatsColumns("brand")} />

            <Divider style={{ margin: 0 }} />
            <GestRowStats style={{ width: "calc(20vw - 20px)" }} loading={statsLoading} rows={mapStats.communications} columns={rowStatsColumns("communication")} />

            <Divider style={{ margin: 0 }} />
            <GestRowStats style={{ width: "calc(20vw - 20px)" }} loading={statsLoading} rows={mapStats.types} columns={rowStatsColumns("device_type")} />

            <Divider style={{ margin: 0 }} />
            <GestRowStats style={{ width: "calc(20vw - 20px)" }} loading={statsLoading} rows={mapStats.alarms} columns={rowStatsColumns("alarms")} />
        </GestCard>
    </div>;
};
export default React.memo(Home);